import { Button } from '../../atoms/Button/Button';
import { Inline } from '../../atoms/Inline';
import { Text } from '../../atoms/Typography/Text';
import { useTranslation } from '../../../i18n/useTranslation';
import type { GuidedTimer as GuidedTimerState } from '../../../hooks/useGuidedTimer';
import './GuidedTimer.css';

export interface GuidedTimerProps {
  timer: GuidedTimerState;
  /** Shown before the timer runs: the kit's (or the user's own) waiting time. */
  plannedSec: number;
  onStart: () => void;
}

export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Countdown of a test-kit waiting step (wireframe "Guided Test Session"). The
 * time is a `role="timer"` (not announced every second); the end is announced by
 * the page. ±30 s adjust the running time; Skip ends the wait at once.
 */
export function GuidedTimer({ timer, plannedSec, onStart }: GuidedTimerProps) {
  const { t } = useTranslation();
  const running = timer.status === 'running';
  const paused = timer.status === 'paused';
  const active = running || paused;
  const shownSec = active ? timer.remainingSec : timer.status === 'done' ? 0 : plannedSec;
  const progress = active && timer.totalSec > 0 ? 1 - timer.remainingSec / timer.totalSec : timer.status === 'done' ? 1 : 0;

  return (
    <div className="guided-timer" data-testid="guided-timer">
      <Text as="span" variant="caption" color="muted">
        {timer.status === 'done' ? t('guided.timer.finished') : t('guided.timer.remaining')}
      </Text>
      <span className="guided-timer__clock" role="timer" aria-live="off" aria-atomic="true">
        {formatClock(shownSec)}
      </span>
      <div className="guided-timer__track" aria-hidden="true">
        <div className="guided-timer__bar" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
      <Inline gap="2" className="guided-timer__actions">
        {timer.status === 'idle' && (
          <Button variant="primary" size="sm" onClick={onStart}>
            {t('guided.timer.start')}
          </Button>
        )}
        {running && (
          <Button variant="secondary" size="sm" onClick={timer.pause}>
            {t('guided.timer.pause')}
          </Button>
        )}
        {paused && (
          <Button variant="secondary" size="sm" onClick={timer.resume}>
            {t('guided.timer.resume')}
          </Button>
        )}
        {active && (
          <>
            <Button variant="ghost" size="sm" onClick={() => timer.adjust(-30)} aria-label={String(t('guided.timer.minus30Label'))}>
              −30 s
            </Button>
            <Button variant="ghost" size="sm" onClick={() => timer.adjust(30)} aria-label={String(t('guided.timer.plus30Label'))}>
              +30 s
            </Button>
            <Button variant="ghost" size="sm" onClick={timer.skip}>
              {t('guided.timer.skip')}
            </Button>
          </>
        )}
      </Inline>
    </div>
  );
}
